import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Typography,
  Form,
  InputNumber,
  Button,
  Alert,
  Descriptions,
  Progress,
  Space,
  Divider,
  Tag,
  message,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import {
  getRoleLimits,
  updateRoleLimits,
  clearError,
  clearSuccess
} from '../../features/account/limiterSlice';

const { Title, Text } = Typography;

const UserLimits = () => {
  const dispatch = useDispatch();
  const { roleLimits, currentCounts, managedBy, loading, error, success } = useSelector(
    (state) => state.limiter
  );
  
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(getRoleLimits());
  }, [dispatch]);

  useEffect(() => {
    if (roleLimits && Object.keys(roleLimits).length > 0) {
      form.setFieldsValue(roleLimits);
    }
  }, [roleLimits, form]);

  useEffect(() => {
    if (success) {
      message.success('Role limits updated successfully!');
      dispatch(clearSuccess());
      setHasChanges(false);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleSubmit = async (values) => {
    try {
      await dispatch(updateRoleLimits(values)).unwrap();
      // Refresh data after successful update
      dispatch(getRoleLimits());
    } catch (error) {
      // Error is handled by useEffect
    }
  };

  const handleReset = () => {
    form.setFieldsValue(roleLimits);
    setHasChanges(false);
  };

  const getProgressColor = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 90) return '#ff4d4f';
    if (percentage >= 75) return '#faad14';
    return '#52c41a';
  };

  const getProgressPercent = (current, limit) => {
    return limit > 0 ? Math.round((current / limit) * 100) : 0;
  };

  const roleDisplayNames = {
    admin: 'Admin Users',
    devuser: 'Developer Users', 
    desuser: 'Designer Users',
    prouser: 'Pro Users'
  };

  const roleIcons = {
    admin: <SettingOutlined />,
    devuser: <UserOutlined />,
    desuser: <UserOutlined />,
    prouser: <TeamOutlined />
  };

  if (loading && !roleLimits) {
    return (
      <Card loading={true}>
        <div style={{ height: 200 }} />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <TeamOutlined className="text-xl text-white" />
            </div>
            <div>
              <Title level={3} className="mb-1">Role Limits Management</Title>
              <Text className="text-gray-600">Manage user role limits and restrictions</Text>
            </div>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => dispatch(getRoleLimits())}
            loading={loading}
          >
            Refresh
          </Button>
        </div>

        {managedBy && (
          <Alert
            message={`Managed by: ${managedBy.name} (${managedBy.email})`}
            type="info"
            showIcon
            className="mb-4"
          />
        )}
      </Card>

      {/* Current Statistics */}
      <Card 
        title="Current Usage Overview"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Row gutter={[16, 16]}>
          {Object.entries(currentCounts || {}).filter(([role]) => role !== 'visitor').map(([role, count]) => {
            const limit = roleLimits[role] || 0;
            const percentage = getProgressPercent(count, limit);
            
            return (
              <Col xs={24} sm={12} lg={8} xl={6} key={role}>
                <Card size="small" className="text-center">
                  <div className="flex justify-center mb-2">
                    {roleIcons[role]}
                  </div>
                  <Statistic
                    title={roleDisplayNames[role]}
                    value={count}
                    suffix={`/ ${limit}`}
                    valueStyle={{ fontSize: '18px' }}
                  />
                  <Progress
                    percent={percentage}
                    strokeColor={getProgressColor(count, limit)}
                    size="small"
                    className="mt-2"
                  />
                  <Tag 
                    color={percentage >= 90 ? 'red' : percentage >= 75 ? 'orange' : 'green'}
                    className="mt-1"
                  >
                    {percentage}% used
                  </Tag>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Limit Settings Form */}
      <Card 
        title="Update Role Limits"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
        >
          <Row gutter={[16, 16]}>
            {Object.entries(roleDisplayNames).map(([role, displayName]) => {
              const currentCount = currentCounts[role] || 0;
              
              return (
                <Col xs={24} sm={12} lg={8} key={role}>
                  <Form.Item
                    label={
                      <div className="flex items-center space-x-2">
                        {roleIcons[role]}
                        <span>{displayName}</span>
                      </div>
                    }
                    name={role}
                    rules={[
                      { required: true, message: `Please set limit for ${displayName}` },
                      { 
                        type: 'number', 
                        min: currentCount, 
                        message: `Limit cannot be less than current count (${currentCount})` 
                      }
                    ]}
                    extra={`Current users: ${currentCount}`}
                  >
                    <InputNumber
                      min={currentCount}
                      max={9999}
                      style={{ width: '100%' }}
                      placeholder={`Set limit for ${displayName}`}
                    />
                  </Form.Item>
                </Col>
              );
            })}
          </Row>

          <Divider />

          <div className="flex justify-between items-center">
            <div>
              {hasChanges && (
                <Text type="warning" className="text-sm">
                  You have unsaved changes
                </Text>
              )}
            </div>
            <Space>
              <Button 
                onClick={handleReset}
                disabled={!hasChanges}
              >
                Reset
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={!hasChanges}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      {/* Help Information */}
      <Card 
        title="Important Information"
        className="shadow-soft border border-border-light rounded-xl"
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Root Users">
            Always limited to 1 user (hardcoded for security)
          </Descriptions.Item>
          <Descriptions.Item label="Role Limits">
            Cannot set limits below current user count for any role
          </Descriptions.Item>
          <Descriptions.Item label="Registration Impact">
            New user registration will be blocked when role limits are reached
          </Descriptions.Item>
          <Descriptions.Item label="Access Control">
            Only Root and Admin users can modify these settings
          </Descriptions.Item>
          <Descriptions.Item label="Visitor Management">
            Visitor limits are managed separately in the Visitor Management section
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default UserLimits;